import React from 'react';
import type {
  AfcSubmission,
  AfcSubmissionCandidateOpportunity,
  CandidateLegalTechSkill,
  GroupedExperiences,
  Taxonomy,
  BioRenderableCandidate,
} from '@axiom/validation';
import { SubmissionCandidateModel } from '@axiom/ui-models';

import TransparentVectorBkg from '../../../assets/graphics/TransparentVectorTop.svg?url';
import { colors } from '../../../theme/axiom-theme';
import { CandidateHighlightExperienceSummary } from '../CandidateHighlightExperienceSummary/CandidateHighlightExperienceSummary';
import { CandidateHighlightExperienceDetails } from '../CandidateHighlightExperienceDetails/CandidateHighlightExperienceDetails';
import { Gutter } from '../../layout/Gutter/Gutter';
import { Card } from '../../layout/Card/Card';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { AxiomLogo } from '../../element/AxiomLogo/AxiomLogo';
import { CandidateHighlightsUtil } from '../../../utils/candidate-highlights-util';
import { ClientEngagementsCandidateHeader } from '../ClientEngagementsCandidateHeader/ClientEngagementsCandidateHeader';
import { CardSection } from '../../layout/Card/CardSection';
import { CandidateDescription } from '../CandidateDescription/CandidateDescription';
import { SmallHeader } from '../../content/SmallHeader/SmallHeader';
import { TalentExperiences } from '../TalentExperiences/TalentExperiences';
import { TalentEducation } from '../TalentEducation/TalentEducation';
import { TalentCertifications } from '../TalentCertifications/TalentCertifications';
import { TalentQualifications } from '../TalentQualifications/TalentQualifications';
import { TalentLanguages } from '../TalentLanguages/TalentLanguages';
import { LegalTechSkillsDisplay } from '../LegalTechSkills/LegalTechSkillsDisplay';
import { CandidateReputationPdfDisplay } from '../CandidateReputationPdfDisplay/CandidateReputationPdfDisplay';
import { TalentExperiencesHighlightedItem } from '../TalentExperiences/TalentExperiencesHighlightedItem';
import { GroupedExperiencesUtil } from '../../../utils/grouped-experiences-util';

export type BioPdfCandidateProps = {
  isDownloading?: boolean;
  candidate: BioRenderableCandidate;
  showCandidateReputation?: AfcSubmissionCandidateOpportunity['showCandidateReputation'];
  candidateReputationClarification?: AfcSubmissionCandidateOpportunity['candidateReputationClarification'];
  submissionDescription?: AfcSubmissionCandidateOpportunity['submissionDescription'];
  groupedExperiences: GroupedExperiences[];
  submission?: AfcSubmission | null;
  isAnonymous?: boolean;
  taxonomy: Taxonomy;
  removeTalentImage?: boolean;
  removeLogo?: boolean;
  hideAllHighlights?: boolean;
  hideHighlightsDetails?: boolean;
  hideLocation?: boolean;
  legalTechSkills: CandidateLegalTechSkill[];
  isAhqBio?: boolean;
};

export const BioPdfCandidate = ({
  isDownloading,
  candidate,
  groupedExperiences,
  submissionDescription,
  submission = null,
  isAnonymous = false,
  taxonomy,
  removeTalentImage = false,
  removeLogo = false,
  hideAllHighlights = false,
  hideHighlightsDetails = false,
  hideLocation = false,
  legalTechSkills,
  isAhqBio = false,
  showCandidateReputation,
  candidateReputationClarification,
}: BioPdfCandidateProps) => {
  const candidateModel = new SubmissionCandidateModel({
    candidate,
    submissionDescription,
  });
  const candidateDescription = candidateModel.calculatedDescription();

  const correctHighlightCalculation = submission
    ? CandidateHighlightsUtil.calculateNumberOfHighlights(candidate, submission)
    : CandidateHighlightsUtil.calculateNumberOfSummaryHighlights(candidate);

  const { highlightedExperiences, regularGroups } =
    GroupedExperiencesUtil.splitHighlighted(groupedExperiences);

  return (
    <Gutter bottom="32px">
      <Card name="TALENT_CONTAINER" shadow={null}>
        <CardSection
          background={{
            image: TransparentVectorBkg,
            color: colors.controlInverse,
            position: 'right top',
            size: 'contain',
          }}
        >
          <Layout position="space-between middle">
            <LayoutItem position="left">
              <ClientEngagementsCandidateHeader
                candidate={candidate}
                /* Printed, so the picture has to travel in the markup rather than as a url. */
                embed
                isAnonymous={isAnonymous}
                removeTalentImage={removeTalentImage}
                removeLocationToggle={hideLocation}
              />
            </LayoutItem>
            {!removeLogo && (
              <LayoutItem position="right">
                <AxiomLogo name="LOGO" height="47px" />
              </LayoutItem>
            )}
          </Layout>
          <Gutter bottom="24px" />
        </CardSection>
        <CardSection divider>
          {!!candidateDescription && !!submission && (
            <Gutter bottom="16px">
              <CandidateDescription
                description={candidateDescription}
                calculatedFirstName={candidate.calculatedFirstName}
                formatting={candidateModel.calculatedDescriptionFormatting()}
              />
            </Gutter>
          )}
          {correctHighlightCalculation > 0 && !hideAllHighlights && (
            <>
              <Gutter bottom="16px">
                <SmallHeader name="highlights-title-pdf">
                  Highlights
                </SmallHeader>
              </Gutter>
              <CandidateHighlightExperienceSummary
                isDownloading={isDownloading}
                candidate={candidate}
                submission={submission}
                taxonomy={taxonomy}
                condensed
              />

              {!hideHighlightsDetails && (
                <Gutter bottom="16px">
                  <CandidateHighlightExperienceDetails
                    candidate={candidate}
                    taxonomy={taxonomy}
                  />
                </Gutter>
              )}
            </>
          )}
        </CardSection>
        {isAhqBio ? null : (
          <CardSection divider>
            <CandidateReputationPdfDisplay
              showCandidateReputation={showCandidateReputation}
              candidateReputationClarification={
                candidateReputationClarification
              }
              candidate={candidate}
            />
          </CardSection>
        )}
        {groupedExperiences.length > 0 && (
          <>
            <CardSection divider>
              <SmallHeader name="EXPERIENCE_LABEL">Experience</SmallHeader>
            </CardSection>
            {highlightedExperiences.length > 0 && (
              <CardSection name="HIGHLIGHTED_EXPERIENCES_SECTION" divider>
                <SmallHeader name="HIGHLIGHTED_EXPERIENCES_HEADER">
                  Highlighted Experiences
                </SmallHeader>
                <Gutter top="16px">
                  {highlightedExperiences.map((exp, i) => (
                    <Gutter key={exp.id} bottom="16px">
                      <TalentExperiencesHighlightedItem
                        name={`HIGHLIGHTED_EXPERIENCE_ITEM_${i}`}
                        experience={exp}
                      />
                    </Gutter>
                  ))}
                </Gutter>
              </CardSection>
            )}
            <TalentExperiences groupedExperiences={regularGroups} />
          </>
        )}
        {legalTechSkills.length > 0 && (
          <CardSection>
            <Gutter bottom="16px">
              <SmallHeader>Legal Tech Skills</SmallHeader>
            </Gutter>
            <LegalTechSkillsDisplay
              legalTechSkills={legalTechSkills}
              taxonomy={taxonomy}
            />
          </CardSection>
        )}
        {(candidate.degrees?.length ?? 0) > 0 && (
          <CardSection>
            <Gutter bottom="16px">
              <SmallHeader>Education</SmallHeader>
            </Gutter>
            <TalentEducation candidate={candidate} />
          </CardSection>
        )}
        {(candidate.certifications?.length ?? 0) > 0 && (
          <CardSection divider>
            <Gutter bottom="16px">
              <SmallHeader>Accomplishments</SmallHeader>
            </Gutter>
            <Gutter bottom="16px">
              <TalentCertifications candidate={candidate} />
            </Gutter>
            <Gutter bottom="16px">
              <TalentQualifications candidate={candidate} />
            </Gutter>
          </CardSection>
        )}
        {(candidate.languages?.length ?? 0) > 0 && (
          <CardSection>
            <Gutter bottom="16px">
              <SmallHeader>Languages</SmallHeader>
            </Gutter>
            <TalentLanguages candidate={candidate} />
          </CardSection>
        )}
      </Card>
    </Gutter>
  );
};
